import { useTranslation } from 'react-i18next'
import React, { useState, useEffect } from 'react'
import styles from './UniversityArchive.module.scss'
import {
  getUniversityFunds,
  getUniversityInventories,
  createUniversityInventory,
  createUniversityFund,
  deleteUniversityFund,
  getUniversityCases,
  createUniversityCase,
  updateUniversityCase,
  deleteUniversityCase,
  createUniversityDocument,
  updateUniversityDocument,
  deleteUniversityDocument,
} from '../../../service/ArchiveService'
import { Skeleton } from 'components/UnvUI'
import Notification from '../../../utils/Notifications'

const ICO_FUND = <svg viewBox="0 0 24 24"><path d="M20 6H4V4h16v2zm1 2H3a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1zm-1 11H4v-9h16v9zm-7-7H9v2h4v-2z"/></svg>
const ICO_LIST = <svg viewBox="0 0 24 24"><path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/></svg>
const ICO_CASE = <svg viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12z"/></svg>
const ICO_DOC = <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
const ICO_PLUS = <svg viewBox="0 0 24 24"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
const ICO_DEL = <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
const ICO_EDIT = <svg viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
const ICO_DL = <svg viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>

const documentTypes = [
  { value: 'order', label: 'Приказ' },
  { value: 'protocol', label: 'Протокол' },
  { value: 'report', label: 'Отчёт' },
  { value: 'contract', label: 'Договор' },
  { value: 'study_plan', label: 'Учебный план' },
  { value: 'correspondence', label: 'Переписка' },
  { value: 'other', label: 'Прочее' },
]

const getDocTypeLabel = (v) => documentTypes.find(t => t.value === v)?.label || v
const getConfLabel = (v) => ({ public: 'Открытый', internal: 'Внутренний', confidential: 'Конфиденциальный', secret: 'Секретный' })[v] || v
const getConfClass = (v) => ({ public: styles.conf_public, internal: styles.conf_internal, confidential: styles.conf_confidential, secret: styles.conf_secret })[v] || ''
const getStatusClass = (v) => v === 'active' ? styles.status_active : v === 'closed' ? styles.status_closed : styles.status_inactive
const fmtDate = (d) => {
  if (!d) return '—'
  if (/^\d{2}\.\d{2}\.\d{4}/.test(d)) return d.slice(0, 10)
  try {
    const dt = new Date(d)
    if (isNaN(dt.getTime())) return d
    return dt.toLocaleDateString('ru-RU')
  } catch { return d }
}

function UniversityArchive() {
  const { t } = useTranslation('common')

  const [funds, setFunds] = useState([])
  const [inventories, setInventories] = useState([])
  const [cases, setCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedFund, setSelectedFund] = useState(null)
  const [selectedInventory, setSelectedInventory] = useState(null)
  const [selectedCase, setSelectedCase] = useState(null)
  const [showFundModal, setShowFundModal] = useState(false)
  const [showInventoryModal, setShowInventoryModal] = useState(false)
  const [showCaseModal, setShowCaseModal] = useState(false)
  const [showDocModal, setShowDocModal] = useState(false)
  const [editingDoc, setEditingDoc] = useState(null)
  const [fundForm, setFundForm] = useState({ fund_number: '', title: '', description: '' })
  const [inventoryForm, setInventoryForm] = useState({ fund: '', inventory_number: '', title: '', description: '' })
  const [caseForm, setCaseForm] = useState({ inventory: '', case_number: '', title: '', description: '', case_date_start: '', case_date_end: '', storage_term: 75, physical_location: '', status: 'active' })
  const [docForm, setDocForm] = useState({ document_type: 'order', title: '', description: '', document_date: '', pages: 0, confidentiality_level: 'public', notes: '' })
  const [uploadFile, setUploadFile] = useState(null)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '', sound: '' })

  useEffect(() => { loadData() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true)
      const [f, inv, c] = await Promise.all([getUniversityFunds(), getUniversityInventories(), getUniversityCases()])
      setFunds(f); setInventories(inv); setCases(c)
    } catch { showNotify(t('auto.universityarchive.err_load', 'Ошибка при загрузке данных'), 'error') }
    finally { setLoading(false) }
  }

  const showNotify = (message, type) => setNotify({ isOpen: true, message, type, sound: type })

  const handleCreateFund = async (e) => {
    e.preventDefault()
    try {
      const n = await createUniversityFund(fundForm)
      setFunds([...funds, n])
      setFundForm({ fund_number: '', title: '', description: '' })
      setShowFundModal(false)
      showNotify(t('auto.universityarchive.fund_created', 'Фонд успешно создан!'), 'success')
    } catch { showNotify(t('auto.universityarchive.err_create_fund', 'Ошибка при создании фонда'), 'error') }
  }

  const handleDeleteFund = async (e, fundId) => {
    e.stopPropagation()
    if (!window.confirm(t('auto.universityarchive.confirm_delete_fund', 'Удалить фонд?'))) return
    try {
      await deleteUniversityFund(fundId)
      setFunds(funds.filter(f => f.id !== fundId))
      if (selectedFund?.id === fundId) { setSelectedFund(null); setSelectedInventory(null); setSelectedCase(null) }
      showNotify(t('auto.universityarchive.fund_deleted', 'Фонд удалён'), 'success')
    } catch { showNotify(t('auto.universityarchive.err_delete_fund', 'Ошибка при удалении фонда'), 'error') }
  }

  const handleCreateInventory = async (e) => {
    e.preventDefault()
    try {
      const n = await createUniversityInventory(inventoryForm)
      setInventories([...inventories, n])
      setInventoryForm({ fund: '', inventory_number: '', title: '', description: '' })
      setShowInventoryModal(false)
      showNotify(t('auto.universityarchive.inv_created', 'Опись успешно создана!'), 'success')
    } catch { showNotify(t('auto.universityarchive.err_create_inv', 'Ошибка при создании описи'), 'error') }
  }

  const handleCreateCase = async (e) => {
    e.preventDefault()
    try {
      const n = await createUniversityCase(caseForm)
      setCases([...cases, n])
      setCaseForm({ inventory: '', case_number: '', title: '', description: '', case_date_start: '', case_date_end: '', storage_term: 75, physical_location: '', status: 'active' })
      setShowCaseModal(false)
      showNotify(t('auto.universityarchive.case_created', 'Дело успешно создано!'), 'success')
    } catch { showNotify(t('auto.universityarchive.err_create_case', 'Ошибка при создании дела'), 'error') }
  }

  const handleDeleteCase = async (e, caseId) => {
    e.stopPropagation()
    if (!window.confirm(t('auto.universityarchive.confirm_delete_case', 'Удалить дело?'))) return
    try {
      await deleteUniversityCase(caseId)
      setCases(cases.filter(c => c.id !== caseId))
      if (selectedCase?.id === caseId) setSelectedCase(null)
      showNotify(t('auto.universityarchive.case_deleted', 'Дело удалено'), 'success')
    } catch { showNotify(t('auto.universityarchive.err_delete_case', 'Ошибка при удалении дела'), 'error') }
  }

  const handleDigitizeCase = async (e, caseItem) => {
    e.stopPropagation()
    try {
      const upd = await updateUniversityCase(caseItem.id, { is_digitized: !caseItem.is_digitized })
      setCases(cases.map(c => c.id === caseItem.id ? upd : c))
      if (selectedCase?.id === caseItem.id) setSelectedCase(upd)
    } catch { showNotify(t('auto.universityarchive.err_generic', 'Ошибка'), 'error') }
  }

  const handleCreateDocument = async (e) => {
    e.preventDefault()
    try {
      const data = { ...docForm, case: selectedCase.id }
      const n = await createUniversityDocument(data, uploadFile)
      const updCases = cases.map(c => c.id === selectedCase.id ? { ...c, documents: [...(c.documents || []), n] } : c)
      setCases(updCases)
      setSelectedCase({ ...selectedCase, documents: [...(selectedCase.documents || []), n] })
      setDocForm({ document_type: 'order', title: '', description: '', document_date: '', pages: 0, confidentiality_level: 'public', notes: '' })
      setUploadFile(null)
      setShowDocModal(false)
      showNotify(t('auto.universityarchive.doc_added', 'Документ добавлен!'), 'success')
    } catch { showNotify(t('auto.universityarchive.err_add_doc', 'Ошибка при добавлении документа'), 'error') }
  }

  const handleUpdateDocument = async (e) => {
    e.preventDefault()
    try {
      const upd = await updateUniversityDocument(editingDoc.id, docForm)
      const updDocs = (selectedCase.documents || []).map(d => d.id === editingDoc.id ? upd : d)
      const updCases = cases.map(c => c.id === selectedCase.id ? { ...c, documents: updDocs } : c)
      setCases(updCases)
      setSelectedCase({ ...selectedCase, documents: updDocs })
      setEditingDoc(null); setShowDocModal(false)
      showNotify(t('auto.universityarchive.doc_updated', 'Документ обновлён!'), 'success')
    } catch { showNotify(t('auto.universityarchive.err_upd_doc', 'Ошибка при обновлении документа'), 'error') }
  }

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm(t('auto.universityarchive.confirm_delete_doc', 'Удалить документ?'))) return
    try {
      await deleteUniversityDocument(docId)
      const updDocs = (selectedCase.documents || []).filter(d => d.id !== docId)
      const updCases = cases.map(c => c.id === selectedCase.id ? { ...c, documents: updDocs } : c)
      setCases(updCases)
      setSelectedCase({ ...selectedCase, documents: updDocs })
      showNotify(t('auto.universityarchive.doc_deleted', 'Документ удалён'), 'success')
    } catch { showNotify(t('auto.universityarchive.err_del_doc', 'Ошибка при удалении документа'), 'error') }
  }

  const openEditDoc = (doc) => {
    setEditingDoc(doc)
    setDocForm({ document_type: doc.document_type, title: doc.title, description: doc.description || '', document_date: doc.document_date || '', pages: doc.pages || 0, confidentiality_level: doc.confidentiality_level || 'public', notes: doc.notes || '' })
    setShowDocModal(true)
  }

  const openCreateDoc = () => {
    setEditingDoc(null)
    setDocForm({ document_type: 'order', title: '', description: '', document_date: '', pages: 0, confidentiality_level: 'public', notes: '' })
    setUploadFile(null)
    setShowDocModal(true)
  }

  if (loading) {
    return <div className={styles.loading_container}><Skeleton variant="table" rows={5} cols={4} /></div>
  }

  const filteredInventories = inventories.filter(inv => inv.fund === selectedFund?.id)
  const filteredCases = cases.filter(c => c.inventory === selectedInventory?.id)

  return (
    <>
      <Notification notify={notify} setNotify={setNotify} />

      {showFundModal && (
        <div className={styles.modal_overlay} onClick={() => setShowFundModal(false)}>
          <div className={styles.modal_box} onClick={e => e.stopPropagation()}>
            <button className={styles.modal_close} onClick={() => setShowFundModal(false)}>×</button>
            <h2>{t('archive.create_fund_modal')}</h2>
            <form onSubmit={handleCreateFund}>
              <div className={styles.form_field}>
                <label>{t('archive.field_fund_no_req')}</label>
                <input type="text" placeholder={t('archive.ph_fund_no')} required value={fundForm.fund_number}
                  onChange={e => setFundForm({ ...fundForm, fund_number: e.target.value })} />
              </div>
              <div className={styles.form_field}>
                <label>{t('archive.field_title_req')}</label>
                <input type="text" placeholder={t('archive.ph_fund_title')} required value={fundForm.title}
                  onChange={e => setFundForm({ ...fundForm, title: e.target.value })} />
              </div>
              <div className={styles.form_field}>
                <label>{t('assignments.description_label')}</label>
                <textarea placeholder={t('archive.ph_fund_description')} value={fundForm.description}
                  onChange={e => setFundForm({ ...fundForm, description: e.target.value })} />
              </div>
              <div className={styles.form_actions}>
                <button type="submit" className={styles.btn_save}>{t('archive.btn_create_fund')}</button>
                <button type="button" className={styles.btn_cancel} onClick={() => setShowFundModal(false)}>{t('auto.universityarchive.cancel', 'Отмена')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showInventoryModal && (
        <div className={styles.modal_overlay} onClick={() => setShowInventoryModal(false)}>
          <div className={styles.modal_box} onClick={e => e.stopPropagation()}>
            <button className={styles.modal_close} onClick={() => setShowInventoryModal(false)}>×</button>
            <h2>{t('auto.universityarchive.create_inv', 'Создание описи — Фонд')} {selectedFund?.fund_number}</h2>
            <form onSubmit={handleCreateInventory}>
              <div className={styles.form_field}>
                <label>{t('archive.fund')}</label>
                <input type="text" disabled value={selectedFund ? `${selectedFund.fund_number} — ${selectedFund.title}` : ''} />
              </div>
              <div className={styles.form_field}>
                <label>{t('archive.field_inventory_no_req')}</label>
                <input type="text" placeholder={t('archive.ph_inventory_no')} required value={inventoryForm.inventory_number}
                  onChange={e => setInventoryForm({ ...inventoryForm, inventory_number: e.target.value })} />
              </div>
              <div className={styles.form_field}>
                <label>{t('archive.field_title_req')}</label>
                <input type="text" placeholder={t('archive.ph_inventory_title')} required value={inventoryForm.title}
                  onChange={e => setInventoryForm({ ...inventoryForm, title: e.target.value })} />
              </div>
              <div className={styles.form_field}>
                <label>{t('assignments.description_label')}</label>
                <textarea placeholder={t('archive.ph_short_description')} value={inventoryForm.description}
                  onChange={e => setInventoryForm({ ...inventoryForm, description: e.target.value })} />
              </div>
              <div className={styles.form_actions}>
                <button type="submit" className={styles.btn_save}>{t('archive.btn_create_inventory')}</button>
                <button type="button" className={styles.btn_cancel} onClick={() => setShowInventoryModal(false)}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCaseModal && (
        <div className={styles.modal_overlay} onClick={() => setShowCaseModal(false)}>
          <div className={styles.modal_box} onClick={e => e.stopPropagation()}>
            <button className={styles.modal_close} onClick={() => setShowCaseModal(false)}>×</button>
            <h2>{t('auto.universityarchive.create_case', 'Создание дела — Опись')} {selectedInventory?.inventory_number}</h2>
            <form onSubmit={handleCreateCase}>
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <label>{t('archive.field_case_no_req_short')}</label>
                  <input type="text" placeholder={t('archive.ph_case_short')} required value={caseForm.case_number}
                    onChange={e => setCaseForm({ ...caseForm, case_number: e.target.value })} />
                </div>
                <div className={styles.form_field}>
                  <label>{t('archive.retention_years')}</label>
                  <input type="number" value={caseForm.storage_term}
                    onChange={e => setCaseForm({ ...caseForm, storage_term: parseInt(e.target.value) })} />
                </div>
              </div>
              <div className={styles.form_field}>
                <label>{t('archive.field_title_req')}</label>
                <input type="text" placeholder={t('archive.ph_case_title')} required value={caseForm.title}
                  onChange={e => setCaseForm({ ...caseForm, title: e.target.value })} />
              </div>
              <div className={styles.form_field}>
                <label>{t('assignments.description_label')}</label>
                <textarea placeholder={t('archive.ph_case_description')} value={caseForm.description}
                  onChange={e => setCaseForm({ ...caseForm, description: e.target.value })} />
              </div>
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <label>{t('archive.field_period_start')}</label>
                  <input type="date" value={caseForm.case_date_start}
                    onChange={e => setCaseForm({ ...caseForm, case_date_start: e.target.value })} />
                </div>
                <div className={styles.form_field}>
                  <label>{t('archive.field_period_end')}</label>
                  <input type="date" value={caseForm.case_date_end}
                    onChange={e => setCaseForm({ ...caseForm, case_date_end: e.target.value })} />
                </div>
              </div>
              <div className={styles.form_field}>
                <label>{t('archive.field_physical_location')}</label>
                <input type="text" placeholder={t('archive.ph_physical_location')} value={caseForm.physical_location}
                  onChange={e => setCaseForm({ ...caseForm, physical_location: e.target.value })} />
              </div>
              <div className={styles.form_actions}>
                <button type="submit" className={styles.btn_save}>{t('archive.btn_create_case')}</button>
                <button type="button" className={styles.btn_cancel} onClick={() => setShowCaseModal(false)}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDocModal && (
        <div className={styles.modal_overlay} onClick={() => setShowDocModal(false)}>
          <div className={styles.modal_box} onClick={e => e.stopPropagation()}>
            <button className={styles.modal_close} onClick={() => setShowDocModal(false)}>×</button>
            <h2>{editingDoc ? t('auto.universityarchive.edit_doc', 'Редактирование документа') : t('auto.universityarchive.add_doc', 'Добавление документа')}</h2>
            <form onSubmit={editingDoc ? handleUpdateDocument : handleCreateDocument}>
              <div className={styles.form_field}>
                <label>{t('archive.field_doc_type_req')}</label>
                <select required value={docForm.document_type} onChange={e => setDocForm({ ...docForm, document_type: e.target.value })}>
                  {documentTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className={styles.form_field}>
                <label>{t('archive.field_title_req')}</label>
                <input type="text" placeholder={t('archive.ph_doc_title')} required value={docForm.title}
                  onChange={e => setDocForm({ ...docForm, title: e.target.value })} />
              </div>
              <div className={styles.form_field}>
                <label>{t('assignments.description_label')}</label>
                <textarea placeholder={t('archive.ph_short_description')} value={docForm.description}
                  onChange={e => setDocForm({ ...docForm, description: e.target.value })} />
              </div>
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <label>{t('archive.field_doc_date_req')}</label>
                  <input type="date" required value={docForm.document_date}
                    onChange={e => setDocForm({ ...docForm, document_date: e.target.value })} />
                </div>
                <div className={styles.form_field}>
                  <label>{t('archive.field_pages')}</label>
                  <input type="number" placeholder="0" value={docForm.pages}
                    onChange={e => setDocForm({ ...docForm, pages: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
              <div className={styles.form_field}>
                <label>{t('archive.field_confidentiality')}</label>
                <select value={docForm.confidentiality_level} onChange={e => setDocForm({ ...docForm, confidentiality_level: e.target.value })}>
                  <option value="public">{t('archive.conf_public')}</option>
                  <option value="internal">{t('archive.conf_internal')}</option>
                  <option value="confidential">{t('archive.conf_confidential_full')}</option>
                  <option value="secret">{t('archive.conf_secret')}</option>
                </select>
              </div>
              <div className={styles.form_field}>
                <label>{t('archive.field_notes')}</label>
                <textarea placeholder={t('archive.ph_notes')} value={docForm.notes}
                  onChange={e => setDocForm({ ...docForm, notes: e.target.value })} />
              </div>
              {!editingDoc && (
                <div className={styles.form_field}>
                  <label>{t('archive.field_doc_file')}</label>
                  <input type="file" onChange={e => setUploadFile(e.target.files[0])} />
                </div>
              )}
              <div className={styles.form_actions}>
                <button type="submit" className={styles.btn_save}>{editingDoc ? t('auto.universityarchive.save', 'Сохранить') : t('auto.universityarchive.add', 'Добавить')}</button>
                <button type="button" className={styles.btn_cancel} onClick={() => setShowDocModal(false)}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className={styles.university_archive}>
        <div className={styles.arch_header}>
          <div className={styles.arch_header_icon}>{ICO_FUND}</div>
          <div className={styles.arch_header_text}>
            <h1>{t('archive.card_university')}</h1>
            <p>{t('archive.univ_archive_desc')}</p>
          </div>
          <button className={styles.btn_create_solid} onClick={() => setShowFundModal(true)}>{ICO_PLUS} {t('auto.universityarchive.create_fund', 'Создать фонд')}</button>
        </div>

        {(selectedFund || selectedInventory || selectedCase) && (
          <div className={styles.breadcrumb}>
            <span className={styles.bc_item} onClick={() => { setSelectedFund(null); setSelectedInventory(null); setSelectedCase(null) }}>{t('auto.universityarchive.funds', 'Фонды')}</span>
            {selectedFund && (
              <>
                <span className={styles.bc_sep}>›</span>
                <span className={selectedInventory ? styles.bc_item : styles.bc_item_active}
                  onClick={() => { setSelectedInventory(null); setSelectedCase(null) }}>
                  {selectedFund.fund_number} — {selectedFund.title}
                </span>
              </>
            )}
            {selectedInventory && (
              <>
                <span className={styles.bc_sep}>›</span>
                <span className={selectedCase ? styles.bc_item : styles.bc_item_active}
                  onClick={() => setSelectedCase(null)}>
                  {selectedInventory.inventory_number} — {selectedInventory.title}
                </span>
              </>
            )}
            {selectedCase && (
              <>
                <span className={styles.bc_sep}>›</span>
                <span className={styles.bc_item_active}>{t('auto.universityarchive.case_label', 'Дело')} {selectedCase.case_number}</span>
              </>
            )}
          </div>
        )}

        <div className={styles.table_section}>
          <div className={styles.section_head}>
            <h2>{ICO_FUND} {t('auto.universityarchive.funds', 'Фонды')} ({funds.length})</h2>
            <button className={styles.btn_create} onClick={() => setShowFundModal(true)}>{ICO_PLUS} {t('auto.universityarchive.create', 'Создать')}</button>
          </div>
          <table className={styles.data_table}>
            <thead>
              <tr>
                <th>{t('archive.col_fund_no')}</th><th>{t('common.name')}</th><th>{t('assignments.description_label')}</th><th>{t('archive.col_inventories')}</th><th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {funds.length === 0 ? (
                <tr className={styles.empty_row}><td colSpan={5}>{t('archive.no_funds')}</td></tr>
              ) : funds.map(fund => (
                <tr key={fund.id} className={selectedFund?.id === fund.id ? styles.row_selected : ''}
                  onClick={() => { setSelectedFund(fund); setSelectedInventory(null); setSelectedCase(null) }}>
                  <td className={styles.td_number}>{fund.fund_number}</td>
                  <td className={styles.td_title}>{fund.title}</td>
                  <td className={styles.td_desc}>{fund.description || '—'}</td>
                  <td className={styles.td_center}><span className={styles.count_badge}>{inventories.filter(i => i.fund === fund.id).length}</span></td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className={styles.td_actions}>
                      <button className={`${styles.btn_row} ${styles.btn_row_red}`} onClick={e => handleDeleteFund(e, fund.id)}>{ICO_DEL} {t('auto.universityarchive.delete', 'Удалить')}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedFund && (
          <div className={styles.table_section}>
            <div className={styles.section_head}>
              <h2>{ICO_LIST} {t('auto.universityarchive.inventories_of_fund', 'Описи фонда')} «{selectedFund.fund_number}» ({filteredInventories.length})</h2>
              <button className={styles.btn_create} onClick={() => {
                setInventoryForm({ ...inventoryForm, fund: selectedFund.id })
                setShowInventoryModal(true)
              }}>{ICO_PLUS} {t('auto.universityarchive.create_inventory', 'Создать опись')}</button>
            </div>
            <table className={styles.data_table}>
              <thead>
                <tr>
                  <th>{t('archive.col_inventory_no')}</th><th>{t('common.name')}</th><th>{t('assignments.description_label')}</th><th>{t('archive.col_cases')}</th><th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventories.length === 0 ? (
                  <tr className={styles.empty_row}><td colSpan={5}>{t('archive.no_inventories')}</td></tr>
                ) : filteredInventories.map(inv => (
                  <tr key={inv.id} className={selectedInventory?.id === inv.id ? styles.row_selected : ''}
                    onClick={() => { setSelectedInventory(inv); setSelectedCase(null) }}>
                    <td className={styles.td_number}>{inv.inventory_number}</td>
                    <td className={styles.td_title}>{inv.title}</td>
                    <td className={styles.td_desc}>{inv.description || '—'}</td>
                    <td className={styles.td_center}><span className={styles.count_badge}>{cases.filter(c => c.inventory === inv.id).length}</span></td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className={styles.td_actions}>
                        <button className={`${styles.btn_row} ${styles.btn_row_orange}`}
                          onClick={e => { e.stopPropagation(); setSelectedInventory(inv); setSelectedCase(null) }}>
                          {t('auto.universityarchive.cases', 'Дела')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedInventory && (
          <div className={styles.table_section}>
            <div className={styles.section_head}>
              <h2>{ICO_CASE} {t('auto.universityarchive.cases_of_inv', 'Дела описи')} «{selectedInventory.inventory_number}» ({filteredCases.length})</h2>
              <button className={styles.btn_create} onClick={() => {
                setCaseForm({ ...caseForm, inventory: selectedInventory.id })
                setShowCaseModal(true)
              }}>{ICO_PLUS} {t('auto.universityarchive.create_case_btn', 'Создать дело')}</button>
            </div>
            <table className={styles.data_table}>
              <thead>
                <tr>
                  <th>{t('archive.col_case_no')}</th><th>{t('common.name')}</th><th>{t('archive.col_period')}</th><th>{t('archive.col_storage')}</th><th>{t('archive.col_location')}</th><th>{t('common.status')}</th><th>{t('archive.col_digitized')}</th><th>{t('archive.col_documents')}</th><th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredCases.length === 0 ? (
                  <tr className={styles.empty_row}><td colSpan={9}>{t('archive.no_cases_inv')}</td></tr>
                ) : filteredCases.map(c => (
                  <tr key={c.id} className={selectedCase?.id === c.id ? styles.row_selected : ''} onClick={() => setSelectedCase(c)}>
                    <td className={styles.td_number}>{c.case_number}</td>
                    <td className={styles.td_title}>{c.title}</td>
                    <td>
                      {c.case_date_start && c.case_date_end
                        ? `${fmtDate(c.case_date_start)} — ${fmtDate(c.case_date_end)}`
                        : c.case_date_start ? fmtDate(c.case_date_start) : '—'}
                    </td>
                    <td className={styles.td_center}>{c.storage_term || '—'} {t('auto.universityarchive.years_short', 'л.')}</td>
                    <td>{c.physical_location || '—'}</td>
                    <td><span className={`${styles.status_badge} ${getStatusClass(c.status)}`}>{c.status === 'active' ? t('auto.universityarchive.active', 'Активно') : c.status === 'closed' ? t('auto.universityarchive.closed', 'Закрыто') : c.status}</span></td>
                    <td className={styles.td_center}>
                      {c.is_digitized
                        ? <span className={styles.digitized_badge}>{t('auto.components_employeearchive.k1')}</span>
                        : <span style={{ color: '#AAAAAA', fontSize: '0.8rem', fontFamily: 'var(--font-base, Inter, sans-serif)' }}>{t('common.no')}</span>}
                    </td>
                    <td className={styles.td_center}><span className={styles.count_badge}>{(c.documents || []).length}</span></td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className={styles.td_actions}>
                        <button className={`${styles.btn_row} ${c.is_digitized ? styles.btn_row_blue : styles.btn_row_green}`}
                          onClick={e => handleDigitizeCase(e, c)}>{c.is_digitized ? t('auto.universityarchive.undigitize', 'Снять') : t('auto.universityarchive.digitize', 'Оцифровать')}</button>
                        <button className={`${styles.btn_row} ${styles.btn_row_red}`}
                          onClick={e => handleDeleteCase(e, c.id)}>{ICO_DEL}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedCase && (
          <div className={styles.docs_section}>
            <div className={styles.docs_head}>
              <h2>{ICO_DOC} {t('auto.universityarchive.docs_of_case', 'Документы дела №')} {selectedCase.case_number} — {selectedCase.title}</h2>
              <button className={styles.btn_create} onClick={openCreateDoc}>{ICO_PLUS} {t('auto.universityarchive.add', 'Добавить')}</button>
            </div>
            <div className={styles.case_info_bar}>
              {selectedCase.case_date_start && <div className={styles.info_chip}><span className={styles.info_chip_label}>{t('archive.info_period_start')}</span><span className={styles.info_chip_value}>{fmtDate(selectedCase.case_date_start)}</span></div>}
              {selectedCase.case_date_end && <div className={styles.info_chip}><span className={styles.info_chip_label}>{t('archive.info_period_end')}</span><span className={styles.info_chip_value}>{fmtDate(selectedCase.case_date_end)}</span></div>}
              <div className={styles.info_chip}><span className={styles.info_chip_label}>{t('archive.info_storage_term')}</span><span className={styles.info_chip_value}>{selectedCase.storage_term} {t('auto.universityarchive.years', 'лет')}</span></div>
              {selectedCase.physical_location && <div className={styles.info_chip}><span className={styles.info_chip_label}>{t('archive.col_location')}</span><span className={styles.info_chip_value}>{selectedCase.physical_location}</span></div>}
              <div className={styles.info_chip}><span className={styles.info_chip_label}>{t('archive.col_digitized')}</span><span className={styles.info_chip_value}>{selectedCase.is_digitized ? t('common.yes', 'Да') : t('common.no', 'Нет')}</span></div>
            </div>
            <table className={styles.data_table}>
              <thead>
                <tr>
                  <th>{t('schedule.type')}</th><th>{t('common.name')}</th><th>{t('assignments.description_label')}</th><th>{t('common.date')}</th><th>{t('archive.col_pages_short')}</th><th>{t('archive.conf_confidential')}</th><th>{t('common.files')}</th><th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {(!selectedCase.documents || selectedCase.documents.length === 0) ? (
                  <tr className={styles.empty_row}><td colSpan={8}>{t('archive.no_documents_add')}</td></tr>
                ) : selectedCase.documents.map(doc => (
                  <tr key={doc.id}>
                    <td><span className={styles.doc_type_badge}>{getDocTypeLabel(doc.document_type)}</span></td>
                    <td className={styles.td_title}>{doc.title}</td>
                    <td className={styles.td_desc}>{doc.description || '—'}</td>
                    <td>{fmtDate(doc.document_date)}</td>
                    <td className={styles.td_center}>{doc.pages > 0 ? doc.pages : '—'}</td>
                    <td><span className={`${styles.confidentiality_badge} ${getConfClass(doc.confidentiality_level)}`}>{getConfLabel(doc.confidentiality_level)}</span></td>
                    <td>
                      {doc.files?.length > 0 ? (
                        <div className={styles.file_links}>
                          {doc.files.map(f => (
                            <a key={f.id} href={f.file} download={f.file_name} className={styles.file_download_link}>
                              {ICO_DL} {f.file_name || t('auto.universityarchive.download', 'Скачать')}
                            </a>
                          ))}
                        </div>
                      ) : '—'}
                    </td>
                    <td>
                      <div className={styles.td_actions}>
                        <button className={`${styles.btn_row} ${styles.btn_row_dark}`} onClick={() => openEditDoc(doc)}>{ICO_EDIT} {t('auto.universityarchive.edit_short', 'Ред.')}</button>
                        <button className={`${styles.btn_row} ${styles.btn_row_red}`} onClick={() => handleDeleteDocument(doc.id)}>{ICO_DEL}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

export default UniversityArchive
